import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Send } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from '../hooks/use-toast';
import { AddressAutocomplete } from '../components/AddressAutocomplete';

const API = process.env.REACT_APP_BACKEND_URL;
const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],['CO','Colorado'],
  ['CT','Connecticut'],['DE','Delaware'],['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],
  ['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],
  ['ME','Maine'],['MD','Maryland'],['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],
  ['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],
  ['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],
  ['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],['SD','South Dakota'],['TN','Tennessee'],
  ['TX','Texas'],['UT','Utah'],['VT','Vermont'],['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],
  ['WI','Wisconsin'],['WY','Wyoming'],['DC','District of Columbia'],['PR','Puerto Rico'],['VI','U.S. Virgin Islands'],
  ['GU','Guam'],['AS','American Samoa'],['AA','Armed Forces (Americas)'],['AE','Armed Forces (Europe)'],['AP','Armed Forces (Pacific)'],
];

const CA_PROVINCES = [
  ['AB','Alberta'],['BC','British Columbia'],['MB','Manitoba'],['NB','New Brunswick'],['NL','Newfoundland and Labrador'],
  ['NS','Nova Scotia'],['NT','Northwest Territories'],['NU','Nunavut'],['ON','Ontario'],['PE','Prince Edward Island'],
  ['QC','Quebec'],['SK','Saskatchewan'],['YT','Yukon'],
];

// Comprehensive country list — Fortune Auto Design ships internationally.
const COUNTRIES = [
  ['US','United States'],['CA','Canada'],['MX','Mexico'],['GB','United Kingdom'],['AU','Australia'],
  ['NZ','New Zealand'],['DE','Germany'],['FR','France'],['IT','Italy'],['ES','Spain'],
  ['NL','Netherlands'],['BE','Belgium'],['CH','Switzerland'],['AT','Austria'],['SE','Sweden'],
  ['NO','Norway'],['DK','Denmark'],['FI','Finland'],['IE','Ireland'],['PT','Portugal'],
  ['PL','Poland'],['CZ','Czech Republic'],['GR','Greece'],['LU','Luxembourg'],['IS','Iceland'],
  ['JP','Japan'],['KR','South Korea'],['SG','Singapore'],['HK','Hong Kong'],['TW','Taiwan'],
  ['MY','Malaysia'],['TH','Thailand'],['PH','Philippines'],['ID','Indonesia'],['VN','Vietnam'],
  ['IN','India'],['CN','China'],['AE','United Arab Emirates'],['SA','Saudi Arabia'],['QA','Qatar'],
  ['KW','Kuwait'],['BH','Bahrain'],['OM','Oman'],['IL','Israel'],['TR','Turkey'],
  ['ZA','South Africa'],['EG','Egypt'],['MA','Morocco'],['NG','Nigeria'],['KE','Kenya'],
  ['BR','Brazil'],['AR','Argentina'],['CL','Chile'],['CO','Colombia'],['PE','Peru'],
  ['CR','Costa Rica'],['PA','Panama'],['DO','Dominican Republic'],['JM','Jamaica'],['BS','Bahamas'],
  ['PR','Puerto Rico'],
];

const Field = ({ label, name, type = 'text', value, onChange, required, autoComplete, options, placeholder, testId }) => (
  <label className="block">
    <span className="font-display tracking-[0.18em] text-[10px] text-neutral-500 uppercase">{label}{required && <span className="text-[#FF6B00]">*</span>}</span>
    {options ? (
      <select
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        autoComplete={autoComplete}
        data-testid={testId}
        style={{ colorScheme: 'light', WebkitTextFillColor: '#111', color: '#111', backgroundColor: '#ffffff' }}
        className="mt-1.5 w-full border border-neutral-300 bg-white text-neutral-900 px-3.5 py-3 text-sm focus:border-[#FF6B00] focus:outline-none focus:ring-2 focus:ring-[#FF6B00]/15 transition-colors"
      >
        <option value="">Select…</option>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    ) : (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        autoComplete={autoComplete}
        placeholder={placeholder}
        data-testid={testId}
        style={{ colorScheme: 'light', WebkitTextFillColor: '#111', color: '#111', backgroundColor: '#ffffff' }}
        className="mt-1.5 w-full border border-neutral-300 bg-white text-neutral-900 placeholder:text-neutral-400 px-3.5 py-3 text-sm focus:border-[#FF6B00] focus:outline-none focus:ring-2 focus:ring-[#FF6B00]/15 transition-colors"
      />
    )}
  </label>
);

const Checkout = () => {
  const navigate = useNavigate();
  const { items, subtotal, clear, closeCart } = useCart();

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);  // suppress empty-cart redirect after a successful submit
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    postcode: '',
    country: 'US',
    customer_note: '',
  });

  // Close the cart drawer once when landing on checkout. Mount-only — do NOT
  // depend on closeCart's identity, or reopening the cart here snaps it shut.
  useEffect(() => {
    closeCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (submitted || items.length) return undefined;
    // Wait a beat so the cart can hydrate from localStorage (e.g. on a page
    // refresh) before bouncing an "empty" cart back home.
    const t = setTimeout(() => { if (!items.length && !submitted) navigate('/'); }, 600);
    return () => clearTimeout(t);
  }, [items.length, navigate, submitted]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const orderLineItems = useMemo(() => {
    return items.map((it) => ({
      slug: it.slug,
      quantity: it.quantity,
      name: it.name,
      variant: it.variantOption || '',
      sku: it.variantSku || '',
      price: it.price,
      image: it.image,
    }));
  }, [items]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/checkout/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: orderLineItems,
          billing: {
            first_name: form.first_name,
            last_name: form.last_name,
            email: form.email,
            phone: form.phone,
            address_1: form.address_1,
            address_2: form.address_2,
            city: form.city,
            state: form.state,
            postcode: form.postcode,
            country: form.country,
          },
          customer_note: form.customer_note,
        }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.detail || 'Could not submit your request.');
      }
      const data = await res.json();
      try { sessionStorage.setItem('lastOrderItems', JSON.stringify(items)); } catch (_) { /* private mode */ }
      setSubmitted(true);
      clear();
      navigate(`/thank-you?order=${encodeURIComponent(data.fortune_order_number || '')}&email=${encodeURIComponent(form.email || '')}&total=${encodeURIComponent(data.total || '')}&from=checkout`);
    } catch (err) {
      setSubmitting(false);
      toast({ title: 'Could not submit', description: String(err.message || err) });
    }
  };

  if (!items.length) return null;

  return (
    <div className="bg-white min-h-screen" style={{ colorScheme: 'light' }}>
      <section className="pt-36 md:pt-36 pb-8 border-b border-neutral-100">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10">
          <Link to="/" className="inline-flex items-center gap-2 text-xs text-neutral-500 hover:text-[#FF6B00] font-display tracking-[0.18em] mt-2 md:mt-0">
            <ArrowLeft size={14} /> CONTINUE SHOPPING
          </Link>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight mt-4 text-neutral-900">SUBMIT YOUR ORDER</h1>
          <p className="text-sm text-neutral-500 mt-2 max-w-xl">
            Enter your details and submit your order request. Our team will review availability and follow up with a <strong className="text-neutral-700">secure invoice and payment details</strong> — no payment is taken now. Prices are shown in USD.
          </p>
        </div>
      </section>

      <section className="py-10">
        <form onSubmit={onSubmit} className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 grid lg:grid-cols-[1.4fr_1fr] gap-10 lg:gap-14">
          {/* Left: form */}
          <div className="space-y-10">
            {/* Contact */}
            <fieldset>
              <legend className="font-display tracking-[0.22em] text-sm text-[#FF6B00] font-semibold mb-4 pl-3 border-l-2 border-[#FF6B00]">CONTACT INFORMATION</legend>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="First name" name="first_name" value={form.first_name} onChange={onChange} required autoComplete="given-name" testId="co-first-name" />
                <Field label="Last name" name="last_name" value={form.last_name} onChange={onChange} required autoComplete="family-name" testId="co-last-name" />
                <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required autoComplete="email" testId="co-email" />
                <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={onChange} required autoComplete="tel" testId="co-phone" />
              </div>
            </fieldset>

            {/* Shipping address */}
            <fieldset>
              <legend className="font-display tracking-[0.22em] text-sm text-[#FF6B00] font-semibold mb-4 pl-3 border-l-2 border-[#FF6B00]">SHIPPING ADDRESS</legend>
              <div className="space-y-4">
                <label className="block">
                  <span className="font-display tracking-[0.18em] text-[10px] text-neutral-500 uppercase">Street address<span className="text-[#FF6B00]">*</span></span>
                  <AddressAutocomplete
                    apiKey={MAPS_KEY}
                    onSelect={(addr) => setForm((f) => ({
                      ...f,
                      address_1: addr.address_1 || f.address_1,
                      city: addr.city || f.city,
                      state: addr.state || f.state,
                      postcode: addr.postcode || f.postcode,
                      country: addr.country || f.country,
                    }))}
                    inputProps={{
                      name: 'address_1',
                      value: form.address_1,
                      onChange,
                      required: true,
                      autoComplete: 'off',
                      placeholder: 'Start typing your address…',
                      'data-testid': 'co-address1',
                      style: { colorScheme: 'light', WebkitTextFillColor: '#111', color: '#111', backgroundColor: '#ffffff' },
                      className: 'mt-1.5 w-full border border-neutral-300 bg-white text-neutral-900 placeholder:text-neutral-400 px-3.5 py-3 text-sm focus:border-[#FF6B00] focus:outline-none focus:ring-2 focus:ring-[#FF6B00]/15 transition-colors',
                    }}
                  />
                </label>
                <Field label="Apartment / Suite (optional)" name="address_2" value={form.address_2} onChange={onChange} autoComplete="address-line2" testId="co-address2" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="City" name="city" value={form.city} onChange={onChange} required autoComplete="address-level2" testId="co-city" />
                  {form.country === 'US' ? (
                    <Field label="State" name="state" value={form.state} onChange={onChange} required autoComplete="address-level1" options={US_STATES} testId="co-state" />
                  ) : form.country === 'CA' ? (
                    <Field label="Province" name="state" value={form.state} onChange={onChange} required autoComplete="address-level1" options={CA_PROVINCES} testId="co-state" />
                  ) : (
                    <Field label="State / Province / Region" name="state" value={form.state} onChange={onChange} required autoComplete="address-level1" placeholder="e.g. Greater London" testId="co-state" />
                  )}
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="ZIP / Postal code" name="postcode" value={form.postcode} onChange={onChange} required autoComplete="postal-code" testId="co-postcode" />
                  <Field label="Country" name="country" value={form.country} onChange={(e) => { onChange(e); setForm((f) => ({ ...f, state: '' })); }} required autoComplete="country" options={COUNTRIES} testId="co-country" />
                </div>
              </div>
            </fieldset>

            {/* Order note */}
            <fieldset>
              <legend className="font-display tracking-[0.22em] text-sm text-[#FF6B00] font-semibold mb-4 pl-3 border-l-2 border-[#FF6B00]">ORDER NOTES (OPTIONAL)</legend>
              <textarea
                name="customer_note"
                value={form.customer_note}
                onChange={onChange}
                rows={3}
                placeholder="Vehicle year/trim, finish preference, anything we should know…"
                data-testid="co-note"
                style={{ colorScheme: 'light', WebkitTextFillColor: '#111', color: '#111', backgroundColor: '#ffffff' }}
                className="w-full border border-neutral-300 bg-white text-neutral-900 placeholder:text-neutral-400 px-3.5 py-3 text-sm focus:border-[#FF6B00] focus:outline-none focus:ring-2 focus:ring-[#FF6B00]/15 transition-colors"
              />
            </fieldset>
          </div>

          {/* Right: order summary */}
          <aside>
            <div className="bg-neutral-50 border border-neutral-200 p-6 sticky top-32">
              <div className="font-display tracking-[0.22em] text-[10px] text-[#FF6B00] font-semibold mb-3">YOUR ORDER</div>
              <ul className="divide-y divide-neutral-200 mb-5">
                {items.map((it) => (
                  <li key={it.lineId} className="py-3 flex gap-3" data-testid={`co-line-${it.productId}`}>
                    {it.image && <img src={it.image} alt="" className="w-14 h-14 object-cover border border-neutral-200" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-[12px] tracking-wide leading-snug text-neutral-900">{it.name}</div>
                      {it.variantOption && <div className="text-[10px] text-neutral-500 mt-0.5">{it.variantOption}</div>}
                      <div className="text-[11px] text-neutral-500 mt-1">Qty {it.quantity}</div>
                    </div>
                    <div className="text-sm font-display tabular-nums text-neutral-900 whitespace-nowrap">${(it.price * it.quantity).toLocaleString()}</div>
                  </li>
                ))}
              </ul>
              <div className="flex justify-between items-baseline border-t border-neutral-200 pt-4">
                <span className="font-display tracking-[0.22em] text-[11px] text-neutral-500">SUBTOTAL</span>
                <span className="font-display text-xl tabular-nums text-neutral-900">${subtotal.toLocaleString()} <span className="text-xs text-neutral-500">USD</span></span>
              </div>
              <p className="text-[11px] text-neutral-500 leading-relaxed mt-4">
                <strong className="text-neutral-900">No payment now.</strong> Submit your request and our team will confirm availability and email you a secure invoice with payment details. Shipping is billed separately when your build is ready. Lead times vary by product &mdash; please <a href="/contact" className="underline">contact us</a> for specifics.
              </p>

              <button
                type="submit"
                disabled={submitting}
                data-testid="co-submit"
                className={`w-full mt-6 py-4 font-display tracking-[0.22em] text-sm font-bold transition-colors inline-flex items-center justify-center gap-2 ${
                  submitting
                    ? 'bg-neutral-300 text-neutral-600 cursor-not-allowed'
                    : 'bg-[#FF6B00] hover:bg-[#E25500] text-white'
                }`}
              >
                {submitting ? <><Loader2 size={16} className="animate-spin" /> SUBMITTING…</> : <><Send size={15} /> SUBMIT ORDER REQUEST</>}
              </button>

              <p className="text-[11px] text-neutral-400 text-center mt-4 leading-relaxed w-full">
                By submitting, you&rsquo;ll receive a confirmation email. A member of our team will be in touch shortly with your invoice.
              </p>
            </div>
          </aside>
        </form>
      </section>
    </div>
  );
};

export default Checkout;
